// Workspace → Admin → Test data: preview + hide test teams/users from pick lists (nothing deleted).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Loader2, RotateCcw, Sparkles } from "lucide-react";
import api from "../../../lib/api";

function List({ title, rows, render, testid }) {
  const open = rows.filter((r) => !r.hidden).length;
  return (
    <div className="ws-card" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono mb-2">{title} · {rows.length} found · {rows.length - open} hidden</div>
      <div className="flex flex-wrap gap-1.5 max-h-[220px] overflow-y-auto">
        {rows.map((r) => (
          <span key={r.id} className={`px-2 py-0.5 rounded-full border text-[11px] ${r.hidden ? "border-white/5 text-slate-600 line-through" : "border-amber-500/30 text-amber-100"}`}>{render(r)}</span>
        ))}
        {!rows.length && <span className="text-[12px] text-slate-500">None found.</span>}
      </div>
    </div>
  );
}

export default function TestDataCleanup() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/test-cleanup").then(({ data }) => setD(data)).catch(() => toast.error("Could not load test data"));
  useEffect(() => { load(); }, []);
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const { data } = await fn(); toast.success(msg(data)); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(null); }
  };
  if (!d) return <div className="text-slate-500 text-sm py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Scanning…</div>;
  const pending = d.users.filter((u) => !u.hidden).length + d.teams.filter((t) => !t.hidden).length;
  return (
    <div className="space-y-3" data-testid="ws-test-cleanup">
      <div className="ws-card flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[14px] font-semibold text-white">Test data cleanup</div>
          <div className="text-[12px] text-slate-400">Hides test teams and test users from team chips, assignee lists, CW Squad and "Viewing as". Nothing is deleted and test logins keep working.</div>
        </div>
        <label className="inline-flex items-center gap-2 text-[12px] text-slate-300 cursor-pointer" data-testid="ws-test-cleanup-auto">
          <input type="checkbox" checked={d.auto} disabled={!!busy} className="accent-amber-500 w-4 h-4"
            onChange={(e) => run("auto", () => api.patch("/admin/test-cleanup/settings", { auto: e.target.checked }), (r) => r.auto ? "Auto-hide on" : "Auto-hide off")} />
          <Sparkles className="w-3.5 h-3.5 text-amber-300" /> Auto-hide new test data
        </label>
        <button type="button" disabled={!!busy || !pending} data-testid="ws-test-cleanup-hide"
          onClick={() => run("hide", () => api.post("/admin/test-cleanup/hide"), (r) => `Hidden ${r.teams} teams · ${r.users} users`)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 disabled:bg-white/5 disabled:text-slate-500 text-slate-900 font-mono uppercase tracking-widest text-[11px]">
          {busy === "hide" ? <Loader2 className="w-3 h-3 animate-spin" /> : <EyeOff className="w-3 h-3" />} Hide all ({pending})
        </button>
        <button type="button" disabled={!!busy} data-testid="ws-test-cleanup-undo"
          onClick={() => run("undo", () => api.post("/admin/test-cleanup/undo"), (r) => `Restored ${r.teams} teams · ${r.users} users`)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-white/10 text-slate-300 hover:text-white text-[11px] font-mono uppercase tracking-widest">
          <RotateCcw className="w-3 h-3" /> Show hidden
        </button>
      </div>
      <List title="Test teams" rows={d.teams} render={(t) => `${t.name} (${t.members})`} testid="ws-test-cleanup-teams" />
      <List title="Test users" rows={d.users} render={(u) => u.name ? `${u.name} · ${u.email}` : u.email} testid="ws-test-cleanup-users" />
    </div>
  );
}
